import { useState } from "react";
import {
  applyFoodRetailNormalization,
  fetchFoodRetailNormalizationReviewQueue,
  previewFoodRetailNormalization,
} from "../../../../api/foodApi";
import { FOOD_SOURCE_KEYS } from "../../../../constants/foodSourceConstants";
import AdminTableContainer from "../layout/AdminTableContainer";
import { formatCopPrice, formatDateTime } from "./foodRetailIngestionHelpers";

const FoodRetailNormalizationSection = ({ sourceKey = FOOD_SOURCE_KEYS.ARA_COLOMBIA }) => {
  const [limit, setLimit] = useState(100);
  const [minConfidence, setMinConfidence] = useState(0.8);
  const [previewResult, setPreviewResult] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [reviewItems, setReviewItems] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingApply, setLoadingApply] = useState(false);
  const [loadingReview, setLoadingReview] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [applyConfirmed, setApplyConfirmed] = useState(false);

  const handlePreview = async () => {
    try {
      setLoadingPreview(true);
      setErrorMessage("");
      const result = await previewFoodRetailNormalization({
        sourceKey,
        limit: Number(limit) || 100,
        minConfidence: Number(minConfidence) || 0.8,
      });
      setPreviewResult(result);
      setApplyResult(null);
    } catch (error) {
      setPreviewResult(null);
      setErrorMessage(error.message || "Unable to preview normalization.");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleApply = async () => {
    if (!applyConfirmed) {
      setErrorMessage("Confirm apply auto-matches before writing to the database.");
      return;
    }
    try {
      setLoadingApply(true);
      setErrorMessage("");
      const result = await applyFoodRetailNormalization({
        sourceKey,
        limit: Number(limit) || 100,
        minConfidence: Number(minConfidence) || 0.8,
        applyOnlyAutoMatches: true,
      });
      setApplyResult(result);
    } catch (error) {
      setApplyResult(null);
      setErrorMessage(error.message || "Unable to apply normalization.");
    } finally {
      setLoadingApply(false);
    }
  };

  const handleLoadReview = async () => {
    try {
      setLoadingReview(true);
      setErrorMessage("");
      const result = await fetchFoodRetailNormalizationReviewQueue({
        sourceKey,
        includeUnmatched: true,
        limit: 25,
      });
      setReviewItems(result.items || []);
    } catch (error) {
      setReviewItems([]);
      setErrorMessage(error.message || "Unable to load review queue.");
    } finally {
      setLoadingReview(false);
    }
  };

  const matchedSamples = (previewResult?.results || [])
    .filter((row) => row.status === "MATCHED")
    .slice(0, 5);
  const reviewSamples = (previewResult?.results || [])
    .filter((row) => row.status === "NEEDS_REVIEW" || row.status === "LOW_CONFIDENCE")
    .slice(0, 5);

  return (
    <section className="panel-card food-retail-ingestion-page__normalization">
      <div className="food-retail-ingestion-page__section-header">
        <div>
          <h2>ARA Product Normalization</h2>
          <p className="muted">
            Map raw retail observations to canonical staple products for {sourceKey}.
          </p>
        </div>
      </div>

      <div className="food-retail-ingestion-page__grid">
        <label>
          Limit
          <input
            type="number"
            min={1}
            max={1000}
            value={limit}
            onChange={(event) => setLimit(event.target.value)}
          />
        </label>
        <label>
          Minimum confidence
          <input
            type="number"
            min={0}
            max={1}
            step={0.05}
            value={minConfidence}
            onChange={(event) => setMinConfidence(event.target.value)}
          />
        </label>
      </div>

      <div className="food-retail-ingestion-page__enablement-buttons">
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          disabled={loadingPreview || !sourceKey}
          onClick={handlePreview}
        >
          {loadingPreview ? "Previewing…" : "Preview Normalization"}
        </button>
        <label className="checkbox-inline">
          <input
            type="checkbox"
            checked={applyConfirmed}
            onChange={(event) => setApplyConfirmed(event.target.checked)}
          />
          Confirm apply auto-matches
        </label>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={loadingApply || !sourceKey || !applyConfirmed}
          onClick={handleApply}
        >
          {loadingApply ? "Applying…" : "Apply Auto-Matches"}
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={loadingReview || !sourceKey}
          onClick={handleLoadReview}
        >
          {loadingReview ? "Loading…" : "Load Review Queue"}
        </button>
      </div>

      {errorMessage ? <p className="food-retail-ingestion-page__form-errors">{errorMessage}</p> : null}

      {previewResult?.matchedByCanonical?.length ? (
        <>
          <h3>Matched by canonical product</h3>
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Canonical</th>
                  <th>Matches</th>
                  <th>Avg confidence</th>
                  <th>Samples</th>
                </tr>
              </thead>
              <tbody>
                {previewResult.matchedByCanonical.map((row) => (
                  <tr key={row.canonicalProductName}>
                    <td>{row.canonicalProductName}</td>
                    <td>{row.matchCount}</td>
                    <td>
                      {row.averageConfidence != null
                        ? `${Math.round(row.averageConfidence * 100)}%`
                        : "—"}
                    </td>
                    <td>{(row.sampleRawProductNames || []).join("; ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        </>
      ) : null}

      {previewResult?.possibleFalsePositives?.length ? (
        <>
          <h3>Possible false positives</h3>
          <ul className="muted">
            {previewResult.possibleFalsePositives.map((row) => (
              <li key={row.observationId}>
                {row.rawProductName} → {row.matchedProductName}: {row.message}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {previewResult ? (
        <dl className="food-retail-ingestion-page__readiness-grid">
          <div>
            <dt>Total reviewed</dt>
            <dd>{previewResult.totalReviewed}</dd>
          </div>
          <div>
            <dt>Matched</dt>
            <dd>{previewResult.matched}</dd>
          </div>
          <div>
            <dt>Needs review</dt>
            <dd>{previewResult.needsReview}</dd>
          </div>
          <div>
            <dt>No match</dt>
            <dd>{previewResult.noMatch}</dd>
          </div>
        </dl>
      ) : null}

      {applyResult?.matchedProducts?.length ? (
        <>
          <h3>Apply: updated by canonical product</h3>
          <ul className="muted">
            {applyResult.matchedProducts.map((row) => (
              <li key={row.canonicalProductName}>
                {row.canonicalProductName}: {row.updatedCount}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {applyResult ? (
        <dl className="food-retail-ingestion-page__readiness-grid">
          <div>
            <dt>Reviewed</dt>
            <dd>{applyResult.reviewed}</dd>
          </div>
          <div>
            <dt>Updated</dt>
            <dd>{applyResult.updated}</dd>
          </div>
          <div>
            <dt>Matched</dt>
            <dd>{applyResult.matched}</dd>
          </div>
          <div>
            <dt>Needs review</dt>
            <dd>{applyResult.needsReview}</dd>
          </div>
          <div>
            <dt>No match</dt>
            <dd>{applyResult.noMatch}</dd>
          </div>
          <div>
            <dt>Skipped</dt>
            <dd>{applyResult.skipped}</dd>
          </div>
        </dl>
      ) : null}

      {matchedSamples.length ? (
        <>
          <h3>Sample matched rows</h3>
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Raw product</th>
                  <th>Canonical</th>
                  <th>Confidence</th>
                  <th>Includes</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {matchedSamples.map((row) => (
                  <tr key={row.observationId}>
                    <td>{row.rawProductName}</td>
                    <td>{row.matchedProductName}</td>
                    <td>{row.confidence != null ? `${Math.round(row.confidence * 100)}%` : "—"}</td>
                    <td>{(row.includeKeywordsMatched || []).join(", ") || "—"}</td>
                    <td>{row.matchReason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        </>
      ) : null}

      {reviewSamples.length ? (
        <>
          <h3>Sample review-needed rows</h3>
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Raw product</th>
                  <th>Suggested</th>
                  <th>Status</th>
                  <th>Excludes</th>
                  <th>Confidence</th>
                </tr>
              </thead>
              <tbody>
                {reviewSamples.map((row) => (
                  <tr key={row.observationId}>
                    <td>{row.rawProductName}</td>
                    <td>{row.matchedProductName || "—"}</td>
                    <td>{row.status}</td>
                    <td>{(row.excludeKeywordsMatched || []).join(", ") || "—"}</td>
                    <td>{row.confidence != null ? `${Math.round(row.confidence * 100)}%` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        </>
      ) : null}

      {reviewItems.length ? (
        <>
          <h3>Review queue preview</h3>
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Raw product</th>
                  <th>Price</th>
                  <th>Suggested</th>
                  <th>Confidence</th>
                  <th>Captured</th>
                </tr>
              </thead>
              <tbody>
                {reviewItems.map((row) => (
                  <tr key={row.observationId}>
                    <td>{row.rawProductName}</td>
                    <td>{formatCopPrice(row.priceCop)}</td>
                    <td>{row.suggestedProductName || "—"}</td>
                    <td>
                      {row.confidence != null ? `${Math.round(row.confidence * 100)}%` : "—"}
                    </td>
                    <td>{formatDateTime(row.capturedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        </>
      ) : null}
    </section>
  );
};

export default FoodRetailNormalizationSection;
